import { useCallback, useEffect, useRef } from 'react'
import { rastrear, enviarFila } from '../services/tracking'

function nomeDoCampo(alvo) {
  if (!alvo || !['INPUT', 'SELECT', 'TEXTAREA'].includes(alvo.tagName)) return null
  return alvo.name || alvo.id || null
}

function campoPreenchido(alvo) {
  if (alvo.type === 'checkbox' || alvo.type === 'radio') return alvo.checked
  return String(alvo.value || '').trim() !== ''
}

/**
 * Rastreia a interação com um formulário (Links de Campanhas) por delegação:
 * espalhe `handlers` no <form>. Registra visualização, foco e preenchimento de
 * cada campo (com o tempo gasto) e, se a pessoa sair sem concluir, o abandono
 * com o último campo tocado. Nunca envia o valor digitado.
 *
 * `etapa` identifica o formulário ("passo1" | "passo2").
 */
export function useRastreioFormulario(etapa) {
  const estado = useRef({ inicioFoco: {}, focados: new Set(), ultimoCampo: null, concluido: false, pagina: null })

  useEffect(() => {
    const atual = estado.current
    // Fixa a página do formulário: no abandono por navegação interna a URL já mudou.
    atual.pagina = window.location.pathname
    rastrear('form_view', { pagina: atual.pagina, dados: { etapa } })

    const aoSair = () => {
      if (atual.concluido || atual.focados.size === 0) return
      rastrear('form_abandon', {
        pagina: atual.pagina,
        campo: atual.ultimoCampo,
        dados: { etapa, campos_tocados: atual.focados.size }
      })
      enviarFila(true)
    }
    window.addEventListener('pagehide', aoSair)
    return () => {
      window.removeEventListener('pagehide', aoSair)
      // Saída pela navegação interna do site (SPA) também conta como abandono.
      aoSair()
    }
  }, [etapa])

  const onFocusCapture = useCallback((e) => {
    const campo = nomeDoCampo(e.target)
    if (!campo) return
    const atual = estado.current
    atual.inicioFoco[campo] = Date.now()
    atual.ultimoCampo = campo
    if (!atual.focados.has(campo)) {
      atual.focados.add(campo)
      rastrear('field_focus', { campo, dados: { etapa } })
    }
  }, [etapa])

  const onBlurCapture = useCallback((e) => {
    const campo = nomeDoCampo(e.target)
    if (!campo) return
    const atual = estado.current
    const inicio = atual.inicioFoco[campo]
    delete atual.inicioFoco[campo]
    if (!campoPreenchido(e.target)) return
    rastrear('field_fill', {
      campo,
      dados: { etapa, tempo_ms: inicio ? Date.now() - inicio : null }
    })
  }, [etapa])

  /** Erros de validação do react-hook-form (segundo argumento do handleSubmit). */
  const registrarErros = useCallback((erros) => {
    Object.keys(erros || {}).forEach((campo) => {
      rastrear('field_error', { campo, dados: { etapa, tipo: erros[campo] && erros[campo].type } })
    })
  }, [etapa])

  /** Marca o formulário como concluído (não gera abandono ao sair). */
  const concluir = useCallback(() => {
    estado.current.concluido = true
  }, [])

  return { handlers: { onFocusCapture, onBlurCapture }, registrarErros, concluir }
}
